import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { InfoTip } from "@/components/InfoTip";
import {
  CalendarClock, Plus, Trash2, Loader2, Info, ArrowLeft, Send, CheckCircle2,
  AlertTriangle, MessageSquarePlus, Mail,
} from "lucide-react";

const fmtDay = (iso) => {
  if (!iso) return "";
  try { return new Date(iso + "T00:00:00Z").toLocaleDateString(undefined, { weekday: "long", month: "long", day: "2-digit", year: "numeric", timeZone: "UTC" }); }
  catch { return iso; }
};
const fmtMonthDay = (iso) => {
  if (!iso) return "";
  try { return new Date(iso + "T00:00:00Z").toLocaleDateString(undefined, { month: "long", day: "2-digit", year: "numeric", timeZone: "UTC" }); }
  catch { return iso; }
};
const windowText = (w) => {
  const d = fmtDay(w.date);
  if (w.start && w.end) return `${d}, ${w.start}–${w.end}`;
  if (w.start) return `${d}, from ${w.start}`;
  return d;
};

// Deterministic preview mirroring the backend template (subject + plain body).
function buildPreview({ recipientName, address, windows, includeMoveOut, moveOutDate, optionalMessage, senderName }) {
  const subject = `Request to schedule a pre-move-out inspection for ${address || "my rental"}`;
  const winLines = (windows && windows.length ? windows : null)
    ? windows.map((w) => `- ${windowText(w)}`) : ["- (no specific time proposed)"];
  const lines = [
    `Hello ${recipientName || "Property Manager"},`, "",
    `I am preparing to move out of ${address || "my rental"} and would like to ask whether we can schedule a walkthrough before move-out.`, "",
    "Preferred availability:", ...winLines,
  ];
  if (includeMoveOut && moveOutDate) lines.push("", `My recorded move-out date is ${fmtMonthDay(moveOutDate)}.`);
  if (optionalMessage && optionalMessage.trim()) lines.push("", optionalMessage.trim());
  lines.push("", "Please let me know whether one of these times works or suggest another time.", "", "Thank you,", senderName || "The tenant");
  return { subject, body: lines.join("\n") };
}

const STATUS_CHIP = {
  draft: { t: "Draft saved", c: "text-[#475569] bg-slate-100 border-slate-200" },
  sending: { t: "Sending…", c: "text-[#475569] bg-slate-100 border-slate-200" },
  sent: { t: "Request sent", c: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  failed: { t: "Delivery failed", c: "text-amber-700 bg-amber-50 border-amber-200" },
  send_status_unknown: { t: "Delivery not confirmed", c: "text-amber-700 bg-amber-50 border-amber-200" },
};

export function InspectionRequestTool({ propertyId, address, onLogResponse }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading"); // loading | ok | error
  const [mode, setMode] = useState("list"); // list | edit | review
  const [draft, setDraft] = useState(null);
  const [prefillNote, setPrefillNote] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [isRetry, setIsRetry] = useState(false);

  const load = () => {
    setState("loading");
    api.get(`/properties/${propertyId}/inspection-requests`)
      .then((r) => { setData(r.data); setState("ok"); })
      .catch(() => setState("error"));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [propertyId]);

  const archived = data?.archived;
  const requests = data?.requests || [];
  const senderName = user?.name || "The tenant";

  const startNew = () => {
    const pf = data?.recipient_prefill || {};
    const hasPrefill = !!(pf.email || pf.name);
    setDraft({
      request_id: null, recipient_name: pf.name || "", recipient_email: pf.email || "",
      windows: [{ date: "", start: "", end: "" }], include_move_out_date: !!data?.move_out_date,
      optional_message: "",
    });
    setPrefillNote(hasPrefill && !!pf.email);
    setConfirmed(false);
    setIsRetry(false);
    setMode("edit");
  };

  const editDraft = (r) => {
    setDraft({ ...r, windows: r.windows.length ? r.windows : [{ date: "", start: "", end: "" }] });
    setPrefillNote(false); // reloaded draft: origin indeterminate -> neutral "please verify" wording
    setConfirmed(false);
    setIsRetry(false);
    setMode("edit");
  };

  // Retry a failed / unknown request: content is frozen (server rejects edits to non-drafts),
  // so re-send the SAME stored content via review. The send endpoint enforces the retry window.
  const retryRequest = (r) => {
    setDraft({ ...r, windows: r.windows.length ? r.windows : [{ date: "", start: "", end: "" }] });
    setPrefillNote(false);
    setConfirmed(false);
    setIsRetry(true);
    setMode("review");
  };

  const setField = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const setRecipientEmail = (v) => { setPrefillNote(false); setDraft((d) => ({ ...d, recipient_email: v })); };
  const setWindow = (i, k, v) => setDraft((d) => ({ ...d, windows: d.windows.map((w, j) => j === i ? { ...w, [k]: v } : w) }));
  const addWindow = () => setDraft((d) => d.windows.length >= 3 ? d : ({ ...d, windows: [...d.windows, { date: "", start: "", end: "" }] }));
  const removeWindow = (i) => setDraft((d) => ({ ...d, windows: d.windows.filter((_, j) => j !== i) }));

  const cleanWindows = (ws) => ws.filter((w) => w.date).map((w) => ({ date: w.date, start: w.start || null, end: w.end || null }));

  const persist = async () => {
    const body = {
      recipient_name: draft.recipient_name || null,
      recipient_email: draft.recipient_email || null,
      recipient_source: prefillNote ? "prefill" : "manual",
      windows: cleanWindows(draft.windows),
      include_move_out_date: !!draft.include_move_out_date,
      optional_message: draft.optional_message || null,
    };
    if (draft.request_id) {
      const r = await api.patch(`/inspection-requests/${draft.request_id}`, body);
      return r.data;
    }
    const r = await api.post(`/properties/${propertyId}/inspection-requests`, body);
    return r.data;
  };

  const goReview = async () => {
    if (!draft.recipient_email.trim()) { toast.error("Please enter the recipient's email address."); return; }
    setBusy(true);
    try {
      const saved = await persist();
      setDraft((d) => ({ ...d, request_id: saved.request_id }));
      setConfirmed(false);
      setMode("review");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save the request.");
    } finally { setBusy(false); }
  };

  const send = async () => {
    setBusy(true);
    try {
      await api.post(`/inspection-requests/${draft.request_id}/send`, { confirmed: true });
      toast.success("Request sent");
      setMode("list"); setDraft(null); setIsRetry(false); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "The request couldn't be sent right now.");
    } finally { setBusy(false); }
  };

  const deleteDraft = async (rid) => {
    try { await api.delete(`/inspection-requests/${rid}`); load(); if (draft?.request_id === rid) { setMode("list"); setDraft(null); } }
    catch { toast.error("Couldn't delete the draft."); }
  };

  const preview = useMemo(() => draft ? buildPreview({
    recipientName: draft.recipient_name, address, windows: cleanWindows(draft.windows),
    includeMoveOut: draft.include_move_out_date, moveOutDate: data?.move_out_date,
    optionalMessage: draft.optional_message, senderName,
  }) : null, [draft, address, data, senderName]);

  const Card = ({ children }) => (
    <div id="inspection-request-tool" data-testid="inspection-request-tool" className="rounded-2xl border border-[#E2E8F0] bg-white p-5 sm:p-6 theme-card">
      <div className="flex items-center gap-2">
        <CalendarClock size={20} className="text-brand" />
        <h2 className="font-heading text-xl font-semibold text-navy">Request a pre-move-out inspection</h2>
        <InfoTip topic="inspectionRequest" />
      </div>
      <p className="mt-2 text-sm text-[#475569]">Ask your landlord or property manager to schedule a walkthrough before move-out. TenantSeal sends this only when you review it and press send — nothing is sent automatically.</p>
      {children}
    </div>
  );

  if (state === "loading") return (
    <div className="rounded-2xl border border-[#E2E8F0] bg-white p-5 flex items-center justify-center theme-card" data-testid="inspection-request-loading">
      <Loader2 className="animate-spin text-brand" size={18} />
    </div>
  );
  if (state === "error") return (
    <Card>
      <div className="mt-4 flex items-center gap-2 text-sm text-[#64748B]" data-testid="inspection-request-error">
        <AlertTriangle size={15} className="text-amber-500" /> We couldn't load your inspection requests.
        <Button variant="outline" data-testid="inspection-retry" onClick={load} className="ml-2 h-9 rounded-xl">Retry</Button>
      </div>
    </Card>
  );

  // -------- REVIEW & SEND --------
  if (mode === "review" && draft) {
    return (
      <Card>
        <div className="mt-4 rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] p-4" data-testid="inspection-review">
          <p className="text-[11px] font-bold uppercase tracking-wide text-[#94A3B8]">Review before sending</p>
          <div className="mt-2">
            <p className="text-xs font-semibold text-[#64748B]">To</p>
            <p className="text-sm font-semibold text-navy break-all whitespace-normal" data-testid="inspection-review-recipient">
              {draft.recipient_name ? `${draft.recipient_name} · ` : ""}{draft.recipient_email}
            </p>
            {prefillNote ? (
              <p className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-0.5" data-testid="inspection-prefill-note">
                <Info size={11} /> Prefilled from property contact — please verify
              </p>
            ) : (
              <p className="mt-1 text-[11px] text-[#94A3B8]" data-testid="inspection-verify-note">Please verify the recipient before sending.</p>
            )}
          </div>
          <div className="mt-3">
            <p className="text-xs font-semibold text-[#64748B]">Subject</p>
            <p className="text-sm text-navy" data-testid="inspection-review-subject">{preview.subject}</p>
          </div>
          <div className="mt-3">
            <p className="text-xs font-semibold text-[#64748B]">Message</p>
            <pre className="mt-1 whitespace-pre-wrap break-words font-sans text-sm text-[#334155]" data-testid="inspection-review-body">{preview.body}</pre>
          </div>
        </div>

        <label className="mt-4 flex items-start gap-2.5 cursor-pointer" data-testid="inspection-confirm-row">
          <Checkbox checked={confirmed} onCheckedChange={(v) => setConfirmed(!!v)} data-testid="inspection-confirm-checkbox" className="mt-0.5" />
          <span className="text-sm text-navy">I reviewed the recipient, dates and message.</span>
        </label>

        <div className="mt-4 flex flex-wrap gap-3">
          {!isRetry && (
            <Button variant="outline" data-testid="inspection-review-edit" onClick={() => setMode("edit")} className="h-11 rounded-xl">
              <ArrowLeft size={15} className="mr-2" /> Edit
            </Button>
          )}
          {isRetry && (
            <Button variant="outline" data-testid="inspection-review-back" onClick={() => { setMode("list"); setDraft(null); setIsRetry(false); }} className="h-11 rounded-xl">
              <ArrowLeft size={15} className="mr-2" /> Back
            </Button>
          )}
          <Button data-testid="inspection-send-btn" disabled={!confirmed || busy} onClick={send}
            className="h-11 rounded-xl bg-navy hover:bg-navy-hover text-white">
            {busy ? <Loader2 size={15} className="mr-2 animate-spin" /> : <Send size={15} className="mr-2" />} Send request
          </Button>
        </div>
        <p className="mt-3 text-[11px] text-[#94A3B8]">Sending records a neutral scheduling request. Email delivery does not indicate agreement or a response.</p>
      </Card>
    );
  }

  // -------- EDIT DRAFT --------
  if (mode === "edit" && draft) {
    return (
      <Card>
        <div className="mt-4 grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-sm font-medium text-navy">Recipient name (optional)</Label>
            <Input data-testid="inspection-recipient-name" value={draft.recipient_name}
              onChange={(e) => setField("recipient_name", e.target.value)} className="mt-1.5 h-11 rounded-xl" placeholder="Landlord / property manager" />
          </div>
          <div>
            <Label className="text-sm font-medium text-navy">Recipient email</Label>
            <Input data-testid="inspection-recipient-email" value={draft.recipient_email}
              onChange={(e) => setRecipientEmail(e.target.value)} className="mt-1.5 h-11 rounded-xl" placeholder="name@example.com" />
            {prefillNote && (
              <p className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-0.5" data-testid="inspection-prefill-note-edit">
                <Info size={11} /> Prefilled from property contact — please verify
              </p>
            )}
          </div>
        </div>

        <div className="mt-4">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-medium text-navy">Preferred availability (up to 3)</Label>
            {draft.windows.length < 3 && (
              <button data-testid="inspection-add-window" onClick={addWindow} className="inline-flex items-center gap-1 text-xs font-semibold text-brand hover:underline">
                <Plus size={13} /> Add window
              </button>
            )}
          </div>
          <div className="mt-2 space-y-2">
            {draft.windows.map((w, i) => (
              <div key={i} data-testid={`inspection-window-${i}`} className="flex flex-wrap items-end gap-2 rounded-xl border border-[#E2E8F0] p-2.5">
                <div className="flex-1 min-w-[130px]">
                  <span className="text-[10px] font-semibold text-[#94A3B8]">Date</span>
                  <Input type="date" data-testid={`inspection-window-date-${i}`} value={w.date} onChange={(e) => setWindow(i, "date", e.target.value)} className="mt-0.5 h-10 rounded-lg" />
                </div>
                <div className="w-[92px]">
                  <span className="text-[10px] font-semibold text-[#94A3B8]">From</span>
                  <Input type="time" data-testid={`inspection-window-start-${i}`} value={w.start || ""} onChange={(e) => setWindow(i, "start", e.target.value)} className="mt-0.5 h-10 rounded-lg" />
                </div>
                <div className="w-[92px]">
                  <span className="text-[10px] font-semibold text-[#94A3B8]">To</span>
                  <Input type="time" data-testid={`inspection-window-end-${i}`} value={w.end || ""} onChange={(e) => setWindow(i, "end", e.target.value)} className="mt-0.5 h-10 rounded-lg" />
                </div>
                {draft.windows.length > 1 && (
                  <button data-testid={`inspection-remove-window-${i}`} onClick={() => removeWindow(i)} className="h-10 w-10 grid place-items-center rounded-lg text-[#94A3B8] hover:text-red-500"><Trash2 size={15} /></button>
                )}
              </div>
            ))}
          </div>
        </div>

        {data?.move_out_date && (
          <label className="mt-4 flex items-start gap-2.5 cursor-pointer" data-testid="inspection-moveout-row">
            <Checkbox checked={!!draft.include_move_out_date} onCheckedChange={(v) => setField("include_move_out_date", !!v)} data-testid="inspection-moveout-checkbox" className="mt-0.5" />
            <span className="text-sm text-navy">Include my recorded move-out date ({fmtMonthDay(data.move_out_date)})</span>
          </label>
        )}

        <div className="mt-4">
          <Label className="text-sm font-medium text-navy">Optional message</Label>
          <Textarea data-testid="inspection-optional-message" value={draft.optional_message}
            onChange={(e) => setField("optional_message", e.target.value)} rows={3} maxLength={1500}
            className="mt-1.5 rounded-xl" placeholder="Anything you'd like to add (optional)" />
        </div>

        <div className="mt-4 flex flex-wrap gap-3">
          <Button variant="outline" data-testid="inspection-edit-cancel" onClick={() => { setMode("list"); setDraft(null); }} className="h-11 rounded-xl">Cancel</Button>
          <Button data-testid="inspection-review-btn" disabled={busy} onClick={goReview} className="h-11 rounded-xl bg-navy hover:bg-navy-hover text-white">
            {busy ? <Loader2 size={15} className="mr-2 animate-spin" /> : null} Review before sending
          </Button>
        </div>
        <p className="mt-3 text-[11px] text-[#94A3B8]">Saving keeps a draft. Nothing is sent until you review and press send.</p>
      </Card>
    );
  }

  // -------- LIST --------
  return (
    <Card>
      {requests.length === 0 && !archived && (
        <div className="mt-4" data-testid="inspection-empty">
          <p className="text-sm text-[#64748B]">No request yet.</p>
        </div>
      )}

      {requests.length > 0 && (
        <div className="mt-4 space-y-2.5" data-testid="inspection-list">
          {requests.map((r) => {
            const chip = STATUS_CHIP[r.status] || STATUS_CHIP.draft;
            const isSent = r.status === "sent";
            return (
              <div key={r.request_id} data-testid={`inspection-item-${r.request_id}`} className="rounded-xl border border-[#E2E8F0] p-3.5">
                <div className="flex items-center justify-between gap-2">
                  <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border ${chip.c}`} data-testid={`inspection-status-${r.request_id}`}>
                    {isSent ? <CheckCircle2 size={12} /> : chip.t === "Delivery failed" ? <AlertTriangle size={12} /> : <Mail size={12} />} {chip.t}
                  </span>
                  <span className="text-[11px] text-[#94A3B8]">{r.sent_at ? new Date(r.sent_at).toLocaleDateString() : "Draft"}</span>
                </div>
                <p className="mt-1.5 text-sm text-navy break-all" data-testid={`inspection-item-recipient-${r.request_id}`}>
                  To: {r.recipient_name ? `${r.recipient_name} · ` : ""}{r.recipient_email || "(no recipient yet)"}
                </p>
                {isSent && r.sent_snapshot && (
                  <p className="mt-1 text-[11px] text-[#94A3B8]">{r.sent_snapshot.subject}</p>
                )}
                {r.status === "send_status_unknown" && (
                  <p className="mt-1.5 text-[11px] text-[#334155] bg-amber-soft border border-amber-border rounded-lg px-2.5 py-1.5" data-testid={`inspection-unknown-note-${r.request_id}`}>
                    {r.can_retry
                      ? "TenantSeal couldn't confirm whether the email provider accepted this request. You can safely try again within the provider's retry-safety window."
                      : "TenantSeal could not confirm whether the email provider accepted this request. To avoid sending a duplicate, this request cannot be retried after the provider's retry-safety window. Please create a separate new request if you need to send again."}
                  </p>
                )}

                <div className="mt-2.5 flex flex-wrap gap-2">
                  {r.status === "draft" && !archived && (
                    <>
                      <Button size="sm" variant="outline" data-testid={`inspection-edit-${r.request_id}`} onClick={() => editDraft(r)} className="h-9 rounded-lg text-xs">Edit &amp; review</Button>
                      <Button size="sm" variant="outline" data-testid={`inspection-delete-${r.request_id}`} onClick={() => deleteDraft(r.request_id)} className="h-9 rounded-lg text-xs text-red-600 border-red-200"><Trash2 size={13} className="mr-1" /> Delete</Button>
                    </>
                  )}
                  {(r.status === "failed" || r.status === "send_status_unknown") && !archived && r.can_retry && (
                    <Button size="sm" variant="outline" data-testid={`inspection-retry-${r.request_id}`} onClick={() => retryRequest(r)} className="h-9 rounded-lg text-xs">Review &amp; try again</Button>
                  )}
                  {isSent && (
                    <Button size="sm" variant="outline" data-testid={`inspection-log-response-${r.request_id}`} onClick={() => onLogResponse && onLogResponse()} className="h-9 rounded-lg text-xs">
                      <MessageSquarePlus size={13} className="mr-1" /> Log a response
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!archived ? (
        <Button data-testid="inspection-new-btn" onClick={startNew} className="mt-4 h-11 rounded-xl bg-navy hover:bg-navy-hover text-white">
          <Plus size={16} className="mr-2" /> {requests.length ? "New request" : "Prepare a request"}
        </Button>
      ) : (
        <p className="mt-4 inline-flex items-center gap-1.5 text-xs text-[#64748B] bg-slate-50 border border-slate-200 rounded-lg px-3 py-2" data-testid="inspection-archived-note">
          <Info size={13} /> This property is archived — existing requests are read-only.
        </p>
      )}

      <div className="mt-4 flex items-start gap-2 rounded-xl bg-amber-soft border border-amber-border px-4 py-3 text-xs text-[#334155]">
        <Info size={15} className="mt-0.5 shrink-0 text-amber-ink" />
        <span>This is a neutral scheduling request, not legal advice. It doesn't create a legal right to an inspection or a deadline for a response.</span>
      </div>
    </Card>
  );
}
